import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL;

// Axios instance that automatically attaches the Bearer token
export const authAxios = axios.create({ baseURL: API });

export function AuthProvider({ children }) {
    // Access token lives only in memory (not localStorage) for security
    const [accessToken, setAccessToken] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // ------------------------------------------------------------------
    // Internal helpers
    // ------------------------------------------------------------------

    const storeSession = useCallback((tokens) => {
        setAccessToken(tokens.access_token);
        setUser(tokens.user);
        localStorage.setItem('hp_refresh_token', tokens.refresh_token);
    }, []);

    const clearSession = useCallback(() => {
        setAccessToken(null);
        setUser(null);
        localStorage.removeItem('hp_refresh_token');
    }, []);

    // Silently exchange a stored refresh token for a new access token
    const refreshSession = useCallback(async () => {
        const refreshToken = localStorage.getItem('hp_refresh_token');
        if (!refreshToken) {
            setLoading(false);
            return false;
        }
        try {
            const { data } = await axios.post(`${API}/auth/refresh`, {
                refresh_token: refreshToken,
            });
            storeSession(data);
            return true;
        } catch {
            clearSession();
            return false;
        } finally {
            setLoading(false);
        }
    }, [storeSession, clearSession]);

    // Restore session on mount (page reload)
    useEffect(() => {
        refreshSession();
    }, [refreshSession]);

    // Attach access token to every authAxios request
    useEffect(() => {
        const interceptor = authAxios.interceptors.request.use((config) => {
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
            return config;
        });
        return () => authAxios.interceptors.request.eject(interceptor);
    }, [accessToken]);

    // Auto-refresh on 401
    useEffect(() => {
        const interceptor = authAxios.interceptors.response.use(
            (res) => res,
            async (error) => {
                if (error.response?.status === 401 && !error.config._retry) {
                    error.config._retry = true;
                    const ok = await refreshSession();
                    if (ok) {
                        error.config.headers.Authorization = `Bearer ${accessToken}`;
                        return authAxios(error.config);
                    }
                }
                return Promise.reject(error);
            }
        );
        return () => authAxios.interceptors.response.eject(interceptor);
    }, [refreshSession, accessToken]);

    // ------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------

    const login = async (email, password) => {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        storeSession(data);
        return data.user;
    };

    const register = async (email, password, name) => {
        const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
        storeSession(data);
        return data.user;
    };

    const logout = async () => {
        const refreshToken = localStorage.getItem('hp_refresh_token');
        if (refreshToken) {
            try {
                await axios.post(`${API}/auth/logout`, { refresh_token: refreshToken });
            } catch {
                // best-effort revocation
            }
        }
        clearSession();
    };

    return (
        <AuthContext.Provider value={{ user, accessToken, loading, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
